import type { PreferencesSection } from 'src/blocks/preferences-dialog/types';

import type { RatingSectionData, StoredPreferences } from './types';

/**
 * Reorder and filter sections to match the user's stored preferences.
 * Sections missing from preferences are appended at the end (forward-compat:
 * newly introduced sections show by default).
 */
export const applyPreferences = (
  sections: RatingSectionData[],
  preferences: StoredPreferences
): RatingSectionData[] => {
  const prefIds = new Set(preferences.sections.map((s) => s.id));
  const sectionById = new Map(sections.map((s) => [s.id, s]));

  const ordered: RatingSectionData[] = [];

  for (const pref of preferences.sections) {
    const section = sectionById.get(pref.id);
    if (section && pref.enabled) {
      ordered.push(section);
    }
  }

  for (const section of sections) {
    if (!prefIds.has(section.id)) {
      ordered.push(section);
    }
  }

  return ordered;
};

/**
 * Build the dialog's section list by joining stored preferences (order +
 * visibility) with live section data (titles). Sections missing from stored
 * preferences are appended, enabled by default.
 */
export const buildPreferencesSections = (
  sections: RatingSectionData[],
  preferences: StoredPreferences
): PreferencesSection[] => {
  const prefIds = new Set(preferences.sections.map((s) => s.id));
  const sectionById = new Map(sections.map((s) => [s.id, s]));

  const merged: PreferencesSection[] = [];

  for (const pref of preferences.sections) {
    const section = sectionById.get(pref.id);
    if (section) {
      merged.push({
        id: pref.id,
        label: section.title,
        enabled: pref.enabled,
      });
    }
  }

  for (const section of sections) {
    if (!prefIds.has(section.id)) {
      merged.push({
        id: section.id,
        label: section.title,
        enabled: true,
      });
    }
  }

  return merged;
};

/**
 * Strip labels from the dialog's save output so only {id, enabled} is
 * persisted. Order is preserved by the input array.
 */
export const toStoredPreferences = (
  sections: PreferencesSection[]
): StoredPreferences => ({
  sections: sections.map(({ id, enabled }) => ({ id, enabled })),
});
